// In-memory mirror of the IndexedDB tables the UI reads most. Keeping them
// in a store (rather than querying per component) means pages render
// synchronously when you navigate back, so scroll positions restore properly.

import { liveQuery } from 'dexie';
import { create } from 'zustand';
import type { ListKind, RecipeSummary, UserList } from '@dishly/shared';
import { db } from '../lib/db';

interface DataState {
  summaries: RecipeSummary[];
  byId: Map<string, RecipeSummary>;
  summariesLoaded: boolean;
  lists: UserList[];
  listsLoaded: boolean;
}

export const useData = create<DataState>(() => ({
  summaries: [],
  byId: new Map(),
  summariesLoaded: false,
  lists: [],
  listsLoaded: false,
}));

const KIND_ORDER: Record<ListKind, number> = { shopping: 0, favourites: 1, custom: 2 };

export function startDataMirror(): void {
  liveQuery(() => db.summaries.toArray()).subscribe((summaries) => {
    useData.setState({ summaries, byId: new Map(summaries.map((s) => [s.id, s])), summariesLoaded: true });
  });
  liveQuery(() => db.lists.toArray()).subscribe((lists) => {
    lists.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.createdAt.localeCompare(b.createdAt));
    useData.setState({ lists, listsLoaded: true });
  });
}

export function useListOfKind(kind: Exclude<ListKind, 'custom'>): UserList | undefined {
  return useData((s) => s.lists.find((l) => l.kind === kind));
}

export function useSummary(id: string | undefined): RecipeSummary | undefined {
  return useData((s) => (id ? s.byId.get(id) : undefined));
}
